/* Access Models approved product accessory-only cart adapter | rcgs-product-pages-2026-08-04-v3-365b3e526cb6 */
(function(){
'use strict';
let inFlight = null;

function expectedAccessoryKey(prepared) {
  if (!prepared || typeof prepared.releaseId !== 'string' || !Array.isArray(prepared.payload?.items)) return null;
  return `${prepared.releaseId}:${prepared.payload.items.map(item => `${item.id}x${item.quantity}`).join(',')}`;
}

function isPreparedAccessories(prepared, releaseId) {
  if (!prepared || prepared.ok !== true || prepared.accessoryOnly !== true || prepared.surface !== 'product' || prepared.releaseId !== releaseId || !Array.isArray(prepared.payload?.items) || prepared.payload.items.length === 0) return false;
  const ids = new Set();
  for (const item of prepared.payload.items) {
    if (!item || typeof item.id !== 'string' || !/^\d+$/.test(item.id) || !Number.isInteger(item.quantity) || item.quantity < 1 || ids.has(item.id)) return false;
    ids.add(item.id);
  }
  return prepared.accessoryCartKey === expectedAccessoryKey(prepared);
}

function quantities(cart) {
  if (!cart || !Array.isArray(cart.items)) return null;
  const result = new Map();
  for (const item of cart.items) {
    const id = String(item?.variant_id ?? item?.id ?? '');
    if (!/^\d+$/.test(id) || !Number.isInteger(item?.quantity) || item.quantity < 0) continue;
    result.set(id, (result.get(id) || 0) + item.quantity);
  }
  return result;
}

async function readCart(fetchImpl, normalizedRoot, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetchImpl(`${normalizedRoot}cart.js?_rcgs=${Date.now()}`, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' },
      signal: controller.signal,
    });
    if (!response || response.ok !== true) return null;
    return quantities(await response.json());
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

function reconciliationState(before, after, items) {
  if (!(before instanceof Map) || !(after instanceof Map)) return 'unknown';
  if (items.every(item => (after.get(item.id) || 0) >= (before.get(item.id) || 0) + item.quantity)) return 'committed';
  if (items.every(item => (after.get(item.id) || 0) === (before.get(item.id) || 0))) return 'not_committed';
  return 'unknown';
}

async function reconcile(fetchImpl, normalizedRoot, before, items, { cartReadTimeoutMs, reconciliationDelayMs }) {
  if (reconciliationDelayMs > 0) await new Promise(resolve => setTimeout(resolve, reconciliationDelayMs));
  const after = await readCart(fetchImpl, normalizedRoot, cartReadTimeoutMs);
  return reconciliationState(before, after, items);
}

async function submitPreparedAccessories(prepared, {
  compiledEnabled = false,
  datasetLiveAccessoryCartEnabled = false,
  datasetAccessoryCartReleaseState = 'offline_candidate_not_live_approved',
  datasetReleaseId = '',
  adapterReleaseId = '',
  surface = '',
  fetchImpl = globalThis.fetch,
  root = '/',
  timeoutMs = 10000,
  cartReadTimeoutMs = 3000,
  reconciliationDelayMs = 250,
} = {}) {
  if (compiledEnabled !== true) return { ok: false, code: 'ACCESSORY_CART_ADAPTER_COMPILED_OFF' };
  if (datasetLiveAccessoryCartEnabled !== true) return { ok: false, code: 'DATASET_ACCESSORY_CART_DISABLED' };
  if (datasetAccessoryCartReleaseState !== 'live_approved') return { ok: false, code: 'DATASET_ACCESSORY_CART_NOT_LIVE_APPROVED' };
  if (!datasetReleaseId || datasetReleaseId !== adapterReleaseId) return { ok: false, code: 'RELEASE_GATE_FAILED' };
  if (surface !== 'product') return { ok: false, code: 'PRODUCT_SURFACE_REQUIRED' };
  if (!isPreparedAccessories(prepared, datasetReleaseId)) return { ok: false, code: 'ACCESSORIES_NOT_PREPARED' };
  if (typeof fetchImpl !== 'function') return { ok: false, code: 'CART_ADAPTER_UNAVAILABLE' };
  if (!Number.isFinite(timeoutMs) || timeoutMs < 1 || !Number.isFinite(cartReadTimeoutMs) || cartReadTimeoutMs < 1 || !Number.isFinite(reconciliationDelayMs) || reconciliationDelayMs < 0) return { ok: false, code: 'CART_TIMEOUT_CONFIG_INVALID' };
  if (inFlight) {
    if (inFlight.key === prepared.accessoryCartKey) return inFlight.promise;
    return { ok: false, code: 'CART_BUSY' };
  }

  const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
  const promise = (async () => {
    const before = await readCart(fetchImpl, normalizedRoot, cartReadTimeoutMs);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      let response;
      try {
        response = await fetchImpl(`${normalizedRoot}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(prepared.payload),
          signal: controller.signal,
        });
      } catch (err) {
        clearTimeout(timer);
        const state = await reconcile(fetchImpl, normalizedRoot, before, prepared.payload.items, { cartReadTimeoutMs, reconciliationDelayMs });
        if (state === 'committed') return { ok: true, code: 'CART_ADDED_RECONCILED' };
        if (state === 'not_committed') return { ok: false, code: err?.name === 'AbortError' ? 'CART_TIMEOUT_CONFIRMED_NO_COMMIT' : 'CART_NETWORK_ERROR_CONFIRMED_NO_COMMIT' };
        return { ok: false, code: err?.name === 'AbortError' ? 'CART_TIMEOUT_RECONCILIATION_REQUIRED' : 'CART_RECONCILIATION_REQUIRED' };
      }

      if (!response || response.ok !== true) {
        const status = response?.status || 0;
        if (status >= 500 || status === 0) {
          clearTimeout(timer);
          const state = await reconcile(fetchImpl, normalizedRoot, before, prepared.payload.items, { cartReadTimeoutMs, reconciliationDelayMs });
          if (state === 'committed') return { ok: true, code: 'CART_ADDED_RECONCILED' };
          if (state === 'not_committed') return { ok: false, code: 'CART_HTTP_ERROR_CONFIRMED_NO_COMMIT', status };
          return { ok: false, code: 'CART_RECONCILIATION_REQUIRED', status };
        }
        return { ok: false, code: 'CART_HTTP_ERROR', status };
      }
      let value = null;
      try { value = await response.json(); } catch { value = null; }
      return { ok: true, code: 'CART_ADDED', value };
    } finally {
      clearTimeout(timer);
      if (inFlight?.key === prepared.accessoryCartKey) inFlight = null;
    }
  })();
  inFlight = { key: prepared.accessoryCartKey, promise };
  return promise;
}

const ENABLED = true;
const RELEASE_ID = "rcgs-product-pages-2026-08-04-v3-365b3e526cb6";
window.AMRcAccessoryCartAdapter = Object.freeze({
  enabled: ENABLED,
  releaseId: RELEASE_ID,
  submit: function(prepared){ return submitPreparedAccessories(prepared, { compiledEnabled: ENABLED, datasetLiveAccessoryCartEnabled: true, datasetAccessoryCartReleaseState: "live_approved", datasetReleaseId: RELEASE_ID, adapterReleaseId: RELEASE_ID, surface: 'product' }); }
});
})();
